/**
 * Domain-agnostic print pagination math, shared by every measured print
 * surface (chefs orders packet, cafeteria serve sheets). A print document is
 * an ordered list of BLOCKS — each starts on a fresh page and spans
 * `ceil(height / page height)` pages — plus TOC sections pointing at the
 * block they open with. Callers build the structure, measure real block
 * heights off-screen (see the PrintMeasureHost components), then resolve
 * true page numbers here.
 */

// ---------------------------------------------------------------------------
// Print page geometry — derived from the @page rule in globals.css
// (`size: letter portrait; margin: 0.5in`). CSS lays out print at 96px/in, so
// the usable content box is the page minus its margins. These are real
// dimensions, not tuning knobs: change them only if the @page rule changes.
// ---------------------------------------------------------------------------
const PX_PER_IN = 96
const PAGE_WIDTH_IN = 8.5
const PAGE_HEIGHT_IN = 11
const PAGE_MARGIN_IN = 0.5
export const PAGE_CONTENT_WIDTH_PX = (PAGE_WIDTH_IN - 2 * PAGE_MARGIN_IN) * PX_PER_IN // 720
export const PAGE_CONTENT_HEIGHT_PX = (PAGE_HEIGHT_IN - 2 * PAGE_MARGIN_IN) * PX_PER_IN // 960

/**
 * The same letter sheet turned on its side — the box a block gets when its
 * page is landscape (PROD-1279: the USDA production record is eleven
 * columns wide). ADDITIVE on purpose: the portrait constants above are
 * re-exported to the chefs packet and the shopping list, so moving them
 * would repaginate three unrelated surfaces.
 */
export const PAGE_CONTENT_LANDSCAPE_WIDTH_PX = (PAGE_HEIGHT_IN - 2 * PAGE_MARGIN_IN) * PX_PER_IN // 960
export const PAGE_CONTENT_LANDSCAPE_HEIGHT_PX = (PAGE_WIDTH_IN - 2 * PAGE_MARGIN_IN) * PX_PER_IN // 720

/** Which page box a block is laid out on. Omitted means portrait. */
export type BlockOrientation = 'portrait' | 'landscape'

/** A block reference — the id matches the renderer's `data-print-block` attr. */
export interface PageBlockRef {
  id: string
  /** Landscape blocks paginate against the landscape page height. */
  orientation?: BlockOrientation
}

export interface PagePlanTocSection {
  label: string
  /** The block this TOC entry should point at — its first page. */
  firstBlockId: string
  /** 1 = an entry under the one before it (a stop under "Summary for …"). Omitted = top level. */
  depth?: 1
}

/**
 * Ordered blocks (render order, cover first; every block starts on a fresh
 * page) + the TOC sections. Domain layers narrow `blocks` with their own
 * richer block types — anything with an `id` is assignable here.
 */
export interface PagePlanStructure {
  blocks: PageBlockRef[]
  toc: PagePlanTocSection[]
}

export interface TocEntry {
  label: string
  page: number
  /** Carried from the section: 1 = nested under the entry before it. */
  depth?: 1
}

export interface ResolvedPlan {
  toc: TocEntry[]
  totalPages: number
  /** Page span (>=1) each block consumes, by block id. */
  pageSpans: Map<string, number>
  /** First page number (1-based) each block starts on, by block id. */
  startPages: Map<string, number>
}

/**
 * Resolve real page numbers from measured block heights. Each block starts at
 * the top of a fresh page, so it consumes ceil(height / pageHeight) pages.
 * Walking the blocks in order yields each block's start page; the TOC entries
 * read off the start page of the block they point at.
 *
 * ONE page box per orientation: `pageHeightPx` sizes the portrait blocks and
 * `landscapePageHeightPx` the landscape ones. Mixing the two in one job is
 * exactly what the school packet does, and a cover manifest that used one
 * height for both would print wrong page numbers. Both are real dimensions
 * with correct letter defaults — a caller that overrides the page box (a
 * legal sheet, a test) MUST be able to override both, or the argument it
 * passed applies to some of its blocks and not others.
 *
 * Blocks with no measured height fall back to a single page (safe default for
 * fixed, always-short blocks like day covers when measurement is unavailable).
 */
export function resolvePlan(
  structure: PagePlanStructure,
  heights: Map<string, number>,
  pageHeightPx: number = PAGE_CONTENT_HEIGHT_PX,
  landscapePageHeightPx: number = PAGE_CONTENT_LANDSCAPE_HEIGHT_PX,
): ResolvedPlan {
  const startPages = new Map<string, number>()
  const pageSpans = new Map<string, number>()
  let page = 1
  for (const block of structure.blocks) {
    startPages.set(block.id, page)
    const h = heights.get(block.id)
    const blockPageHeight = block.orientation === 'landscape' ? landscapePageHeightPx : pageHeightPx
    const span = h && h > 0 ? Math.max(1, Math.ceil(h / blockPageHeight)) : 1
    pageSpans.set(block.id, span)
    page += span
  }
  const toc: TocEntry[] = structure.toc.map((s) => ({
    label: s.label,
    page: startPages.get(s.firstBlockId) ?? page,
    ...(s.depth ? { depth: s.depth } : {}),
  }))
  return { toc, totalPages: page - 1, pageSpans, startPages }
}
